import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { FlowchartStep } from '../../constants/analysisSteps';

interface FlowchartStepCardProps {
  step: FlowchartStep;
  status: 'pending' | 'running' | 'completed';
  index: number;
}

export function FlowchartStepCard({ step, status, index }: FlowchartStepCardProps) {
  const [expanded, setExpanded] = useState(false);

  const statusIcon = {
    completed: '✓',
    running: '⟳',
    pending: '⏳',
  }[status];

  const statusColor = {
    completed: 'text-green-600',
    running: 'text-blue-600',
    pending: 'text-gray-400',
  }[status];

  const statusBg = {
    completed: 'bg-green-50',
    running: 'bg-blue-50',
    pending: 'bg-gray-50',
  }[status];

  const statusBorder = {
    completed: 'border-green-200',
    running: 'border-blue-200',
    pending: 'border-gray-200',
  }[status];

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${statusBorder} ${statusBg}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-opacity-75 transition-colors ${statusBg}`}
        aria-expanded={expanded}
      >
        <span className={`text-lg font-semibold ${statusColor} flex-shrink-0 w-6 text-center`}>
          {statusIcon}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800 flex-1">
              Step {index}: {step.title}
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white bg-opacity-50 text-gray-600 whitespace-nowrap">
              {step.estimatedDuration}
            </span>
          </div>
          {!expanded && (
            <p className="text-xs text-gray-600 mt-0.5 truncate">
              {step.dataSource}
            </p>
          )}
        </div>

        <div className="flex-shrink-0 text-gray-400">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-opacity-30 space-y-2">
          <div>
            <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
              Data Source
            </p>
            <p className="text-xs text-gray-700 mt-1">{step.dataSource}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
              Phase
            </p>
            <p className="text-xs text-gray-700 mt-1 capitalize">{step.phase}</p>
          </div>
        </div>
      )}
    </div>
  );
}
